/**
 * RedeliveryAttempt - Component for requesting redelivery of failed packages
 */
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { toast } from "sonner";
import api from "../lib/api";
import {
  RefreshCw,
  MapPin,
  Building2,
  Truck,
  CreditCard,
  CheckCircle,
  Loader2,
  DollarSign,
  AlertTriangle
} from "lucide-react";

export default function RedeliveryAttempt({ packageData, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Options, 2: Quote, 3: Payment
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [pudoPoints, setPudoPoints] = useState([]);
  const [formData, setFormData] = useState({
    dropoff_mode: "ADDRESS",
    dropoff_address: packageData?.recipient_address || null,
    dropoff_pudo_id: null,
    service_level: "STANDARD"
  });

  useEffect(() => {
    fetchPudoPoints();
  }, []);

  const fetchPudoPoints = async () => {
    try {
      const res = await api.get("/pudos/active");
      setPudoPoints(res.data.pudos || []);
    } catch (error) {
      console.error("Failed to fetch PUDO points:", error);
    }
  };

  const getQuote = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/packages/${packageData.id}/reattempt/quote`, formData);
      setQuote(res.data);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get quote");
    } finally {
      setLoading(false);
    }
  };

  const initiatePayment = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/packages/${packageData.id}/reattempt/checkout`, {
        ...formData,
        quote_id: quote?.quote_id
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data.session_id) {
        // Handle Stripe or other payment
        toast.success("Redirecting to payment...");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  const canRequestRedelivery = packageData?.status === "held_at_pudo" || 
                               packageData?.status === "delivery_failed";

  if (!canRequestRedelivery) {
    return null;
  }

  return (
    <Card className="bg-amber-500/10 border-amber-500/30 p-4 mt-4">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-5 h-5 text-amber-500" />
        <h3 className="font-semibold text-amber-500">Delivery Failed</h3>
      </div>
      
      <p className="text-sm text-slate-400 mb-4">
        {packageData.status === "held_at_pudo" 
          ? "Your package is being held at a PUDO location. Request redelivery to have it sent to a new address."
          : "Your delivery attempt failed. You can request a new delivery attempt."}
      </p>

      {step === 1 && (
        <div className="space-y-4">
          <div className="text-sm font-medium text-white mb-2">Delivery Option</div>
          
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setFormData(prev => ({ ...prev, dropoff_mode: "ADDRESS" }))}
              className={`p-3 rounded-lg border transition-all ${
                formData.dropoff_mode === "ADDRESS"
                  ? "border-emerald-500 bg-emerald-500/10"
                  : "border-slate-700 hover:border-slate-600"
              }`}
            >
              <MapPin className={`w-5 h-5 mx-auto mb-1 ${
                formData.dropoff_mode === "ADDRESS" ? "text-emerald-500" : "text-slate-400"
              }`} />
              <div className="text-xs text-center">To Address</div>
            </button>
            
            <button
              onClick={() => setFormData(prev => ({ ...prev, dropoff_mode: "PUDO" }))}
              className={`p-3 rounded-lg border transition-all ${
                formData.dropoff_mode === "PUDO"
                  ? "border-emerald-500 bg-emerald-500/10"
                  : "border-slate-700 hover:border-slate-600"
              }`}
            >
              <Building2 className={`w-5 h-5 mx-auto mb-1 ${
                formData.dropoff_mode === "PUDO" ? "text-emerald-500" : "text-slate-400"
              }`} />
              <div className="text-xs text-center">To PUDO</div>
            </button>
          </div>

          {formData.dropoff_mode === "PUDO" && pudoPoints.length > 0 && (
            <select
              value={formData.dropoff_pudo_id || ""}
              onChange={(e) => setFormData(prev => ({ ...prev, dropoff_pudo_id: e.target.value }))}
              className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white"
            >
              <option value="">Select PUDO Point</option>
              {pudoPoints.map(pudo => (
                <option key={pudo.id} value={pudo.id}>
                  {pudo.name} - {pudo.address?.street}
                </option>
              ))}
            </select>
          )}

          <div className="text-sm font-medium text-white mb-2">Service Level</div>
          <div className="grid grid-cols-3 gap-2">
            {["STANDARD", "EXPRESS", "SAME_DAY"].map(level => (
              <button
                key={level}
                onClick={() => setFormData(prev => ({ ...prev, service_level: level }))}
                className={`p-2 rounded-lg border text-xs transition-all ${
                  formData.service_level === level
                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-500"
                    : "border-slate-700 text-slate-400 hover:border-slate-600"
                }`}
              >
                {level.replace("_", " ")}
              </button>
            ))}
          </div>

          <Button
            onClick={getQuote}
            disabled={loading || (formData.dropoff_mode === "PUDO" && !formData.dropoff_pudo_id)}
            className="w-full bg-amber-500 hover:bg-amber-600 text-black"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Get Redelivery Quote
          </Button>
        </div>
      )}

      {step === 2 && quote && (
        <div className="space-y-4">
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <span className="text-slate-400 text-sm">Redelivery Fee</span>
              <span className="text-white font-semibold">${quote.redelivery_fee?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-slate-400 text-sm">Distance</span>
              <span className="text-white">{quote.distance_km?.toFixed(1)} km</span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-slate-400 text-sm">Service</span>
              <span className="text-white">{formData.service_level}</span>
            </div>
            <div className="border-t border-slate-700 pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-white font-medium">Total</span>
                <span className="text-emerald-500 font-bold text-lg">${quote.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => setStep(1)}
              variant="outline"
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={initiatePayment}
              disabled={loading}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Pay & Schedule
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
